import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Sparkles,
  TrendingUp,
  Calendar,
  Activity,
  BarChart3,
  Download,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const AICreditsAnalytics = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  useEffect(() => {
    if (myProfile) {
      fetchData();
    }
  }, [myProfile]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setMyProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, usageRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ai-credits/balance/${myProfile.id}`),
        fetch(`${BACKEND_URL}/api/ai-credits/usage/${myProfile.id}`)
      ]);

      if (balanceRes.ok) {
        const balanceData = await balanceRes.json();
        setBalance(balanceData);
      }

      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setUsage(usageData);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch analytics data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!usage || !balance) return;

    const report = {
      generated_at: new Date().toISOString(),
      researcher_id: myProfile.id,
      subscription_plan: balance.subscription_plan,
      balance: {
        monthly_limit: balance.monthly_limit,
        credits_used: balance.credits_used,
        credits_remaining: balance.credits_remaining,
        purchased_credits: balance.total_purchased_credits,
        reset_date: balance.reset_date
      },
      usage: {
        total_used: usage.total_used,
        by_action: usage.by_action,
        recent_transactions: usage.recent_transactions
      }
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-credits-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();

    toast({
      title: "Report Downloaded",
      description: "Your usage report has been downloaded"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!balance || !usage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>No Data Available</CardTitle>
            <CardDescription>Unable to load AI credits analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={fetchData} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pieData = Object.entries(usage.by_action).map(([action, credits]) => ({
    name: action,
    value: credits
  }));

  const percentage = (balance.credits_used / (balance.monthly_limit + balance.total_purchased_credits)) * 100;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Sparkles className="h-8 w-8 text-purple-600" />
                AI Credits Analytics
              </h1>
              <p className="mt-2 text-gray-600">Track your AI feature usage and credit consumption</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={downloadReport} className="bg-purple-600 hover:bg-purple-700">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Credits Remaining</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{balance.credits_remaining}</div>
              <p className="text-sm text-gray-500 mt-1">of {balance.monthly_limit + balance.total_purchased_credits}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Credits Used</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{balance.credits_used}</div>
              <p className="text-sm text-gray-500 mt-1">{percentage.toFixed(1)}% of limit</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{usage.recent_transactions.length}</div>
              <p className="text-sm text-gray-500 mt-1">This month</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Subscription Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge className="text-lg px-3 py-1">
                {balance.subscription_plan.charAt(0).toUpperCase() + balance.subscription_plan.slice(1)}
              </Badge>
              <p className="text-sm text-gray-500 mt-2">Resets: {new Date(balance.reset_date).toLocaleDateString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different views */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">
              <Activity className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="breakdown">
              <BarChart3 className="h-4 w-4 mr-2" />
              Breakdown
            </TabsTrigger>
            <TabsTrigger value="history">
              <Calendar className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Usage by Action - Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Usage by Action Type</CardTitle>
                  <CardDescription>Distribution of credits across different AI features</CardDescription>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      No usage data yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Action Summary</CardTitle>
                  <CardDescription>Credits consumed per action type</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(usage.by_action).map(([action, credits], index) => (
                      <div key={action} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="font-medium capitalize">{action.replace('_', ' ')}</span>
                        </div>
                        <span className="text-2xl font-bold text-purple-600">{credits}</span>
                      </div>
                    ))}
                    {Object.keys(usage.by_action).length === 0 && (
                      <div className="text-center text-gray-500 py-8">
                        No actions performed yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="breakdown">
            <Card>
              <CardHeader>
                <CardTitle>Credit Usage Breakdown</CardTitle>
                <CardDescription>Visual representation of your AI credit consumption</CardDescription>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={pieData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#8b5cf6" name="Credits Used" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-96 flex items-center justify-center text-gray-500">
                    No usage data to display
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>Recent AI credit usage activities</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {usage.recent_transactions.slice(0, 50).map((tx, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <Sparkles className="h-4 w-4 text-purple-600" />
                          <span className="font-medium capitalize">{tx.action.replace('_', ' ')}</span>
                        </div>
                        {tx.document_name && (
                          <p className="text-sm text-gray-600 mt-1 ml-7">{tx.document_name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-purple-600">-{tx.credits_cost}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(tx.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  {usage.recent_transactions.length === 0 && (
                    <div className="text-center text-gray-500 py-12">
                      <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p>No transaction history yet</p>
                      <p className="text-sm mt-2">Start using AI features to see your activity here</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AICreditsAnalytics;
